import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";

const BlurOverlay = styled.div<{ $left: number; $top: number; $width: number; $height: number }>`
    position: absolute;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    width: ${({ $width }) => $width}px;
    height: ${({ $height }) => $height - 1}px;
    z-index: 1;
    border-radius: 3px;
    background: ${rgba(colorsPalettes.carbon[0], 0.4)};
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const Wrapper = styled.div<{ $hiddenColumns?: string[] }>`
    position: relative;

    ${({ $hiddenColumns }) =>
        $hiddenColumns
            ?.map(
                (col) => `
        td[data-automation-field="${col}"],
        th[data-automation-field-header="${col}"] {
            opacity: 0;
        }
    `,
            )
            .join("")}
`;

interface ITableBlurProps {
    startColumn: string;
    endColumn: string;
    enabled: boolean;
    isLoading: boolean;
    blurContent: ReactNode;
    children: ReactNode;
}

const EMPTY_POSITION = { left: 0, top: 0, width: 0, height: 0 };

export const TableBlur = ({ startColumn, endColumn, enabled, isLoading, blurContent, children }: ITableBlurProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState(EMPTY_POSITION);
    const positionRef = useRef(EMPTY_POSITION);

    useLayoutEffect(() => {
        if (!enabled || isLoading || !wrapperRef.current) {
            return;
        }

        const wrapper = wrapperRef.current;

        const measure = () => {
            const startCell = wrapper.querySelector<HTMLElement>(`th[data-automation-field-header="${startColumn}"]`);
            const endCell = wrapper.querySelector<HTMLElement>(`th[data-automation-field-header="${endColumn}"]`);
            const tableContainer = wrapper.querySelector<HTMLElement>(".table-container");

            if (!startCell || !endCell || !tableContainer) {
                return;
            }

            const wrapperRect = wrapper.getBoundingClientRect();
            const startRect = startCell.getBoundingClientRect();
            const endRect = endCell.getBoundingClientRect();
            const containerRect = tableContainer.getBoundingClientRect();

            const next = {
                left: startRect.left - wrapperRect.left,
                top: startRect.top - wrapperRect.top,
                width: endRect.right - startRect.left,
                height: containerRect.bottom - startRect.top,
            };

            const prev = positionRef.current;
            if (
                prev.left !== next.left ||
                prev.top !== next.top ||
                prev.width !== next.width ||
                prev.height !== next.height
            ) {
                positionRef.current = next;
                setPosition(next);
            }
        };

        measure();

        const mutationObserver = new MutationObserver(measure);
        mutationObserver.observe(wrapper, { childList: true, subtree: true });

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(wrapper);

        window.addEventListener("resize", measure);

        return () => {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [enabled, isLoading, startColumn, endColumn]);

    const isReady = !isLoading && position.width > 0;

    const hiddenColumns = useMemo(
        (): string[] | undefined => (enabled && !isReady ? [startColumn, endColumn] : undefined),
        [enabled, isReady, startColumn, endColumn],
    );

    const overlay =
        enabled && isReady ? (
            <BlurOverlay
                $left={position.left}
                $top={position.top}
                $width={position.width}
                $height={position.height}
            >
                {blurContent}
            </BlurOverlay>
        ) : null;

    return (
        <Wrapper
            ref={wrapperRef}
            $hiddenColumns={hiddenColumns}
        >
            {children}
            {overlay}
        </Wrapper>
    );
};
